import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { API_ENDPOINTS } from '../../app/constants/apiConstants'

interface PatientState {
  currentPatient: any | null
  loading: boolean
  error: string | null
}

const initialState: PatientState = {
  currentPatient: null,
  loading: false,
  error: null,
}

// Async thunk to fetch patient details including billing cycles
export const fetchPatientDetails = createAsyncThunk(
  'patient/fetchDetails',
  async ({ patientUuid, doctorId }: { patientUuid: string; doctorId?: number }, { rejectWithValue }) => {
    try {
      // Build query params if doctorId is provided (for MA)
      const queryParams = doctorId ? `doctor_id=${doctorId}` : null
      const response = await axios.get(API_ENDPOINTS.GET_SPECIFIC_PATIENTS(patientUuid, queryParams))
      return response.data.data?.patient
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch patient details')
    }
  }
)

// Async thunk to refresh patient after device operations
export const refreshPatientAfterDeviceChange = createAsyncThunk(
  'patient/refreshAfterDeviceChange',
  async ({ patientUuid, doctorId }: { patientUuid: string; doctorId?: number }, { rejectWithValue }) => {
    try {
      // Build query params if doctorId is provided (for MA)
      const queryParams = doctorId ? `doctor_id=${doctorId}` : null
      const response = await axios.get(API_ENDPOINTS.GET_SPECIFIC_PATIENTS(patientUuid, queryParams))
      return response.data.data?.patient
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to refresh patient')
    }
  }
)

const patientSlice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    clearCurrentPatient: (state) => {
      state.currentPatient = null
      state.error = null
    },
    updatePatientData: (state, action: PayloadAction<any>) => {
      if (state.currentPatient) {
        state.currentPatient = { ...state.currentPatient, ...action.payload }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch patient details
      .addCase(fetchPatientDetails.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPatientDetails.fulfilled, (state, action) => {
        state.loading = false
        state.currentPatient = action.payload
        state.error = null
      })
      .addCase(fetchPatientDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      // Refresh patient after device change
      .addCase(refreshPatientAfterDeviceChange.fulfilled, (state, action) => {
        // Silent refresh - don't show loading states
        state.currentPatient = action.payload
        state.error = null
      })
  },
})

export const { clearCurrentPatient, updatePatientData } = patientSlice.actions
export default patientSlice.reducer